import { useMemo, useRef, useState, useEffect } from "react";
import {
  PoundSterling,
  AlertTriangle,
  CreditCard,
  TrendingUp,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { formatCreditLimit } from "@/lib/utils";
import { useCustomerTransactions } from "@/hooks/useTransactionsData";
import type { CurrencyMode } from "@/components/shared/currency-toggle";

// Currency symbols lookup — used for display only.
// Both customer-currency and base-currency values are supplied directly
// from the Finance system; no FX arithmetic is performed here.
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  CAD: "CA$",
  AUD: "A$",
};

// Types
interface Customer {
  id: string;
  name: string;
  currency: "USD" | "EUR" | "GBP" | "CAD" | "AUD";
  outstanding: number;
  overdue: number;
  creditLimit: number;
  dso: number;
  lastPayment: string;
  paymentTerms: "Net 15" | "Net 30" | "Net 45" | "Net 60" | "Net 90";
  otherCompaniesCount?: number;
}

interface Payment {
  id: string;
  date: string;
  amount: number;
  invoiceDate?: string;
}

interface CustomerMetricCardsProps {
  customer: Customer;
  payments: Payment[];
  onCardClick: (action: "outstanding" | "overdue" | "credit" | "performance") => void;
  isConsolidatedView?: boolean;
  detailCurrencyMode?: CurrencyMode;
}

// Helper function to format currency with 2 decimal places
const formatCurrencyDetailed = (value: number, currency: string): string => {
  const symbol = CURRENCY_SYMBOLS[currency] ?? "£";
  return `${symbol}${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Helper function to format date
const formatDateShort = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export function CustomerMetricCards({
  customer,
  payments,
  onCardClick,
  isConsolidatedView = false,
  detailCurrencyMode = "customer",
}: Readonly<CustomerMetricCardsProps>) {
  // Fade state for smooth value transition when currency mode changes
  const [isVisible, setIsVisible] = useState(true);
  const prevModeRef = useRef(detailCurrencyMode);
  useEffect(() => {
    if (prevModeRef.current === detailCurrencyMode) return;
    prevModeRef.current = detailCurrencyMode;
    setIsVisible(false);
    const t = setTimeout(() => setIsVisible(true), 150);
    return () => clearTimeout(t);
  }, [detailCurrencyMode]);
  // Fetch transactions summary for accurate balance calculations (uses server-side totals)
  const { transactions: customerTransactions, summary: txSummary } = useCustomerTransactions(customer.id);
  
  // In consolidated view, simulate data from multiple entities (multiply by entity count)
  // In a real implementation, this would fetch data from all entities via API
  const consolidatedMultiplier = isConsolidatedView && customer.otherCompaniesCount 
    ? customer.otherCompaniesCount + 1 
    : 1;

  // Calculate metrics
  const metrics = useMemo(() => {

    // Outstanding Balance Calculation - Use server-side summary when available
    // This avoids incorrect totals when transactions are paginated
    const baseOutstandingBalance = txSummary?.totalOutstanding ?? customerTransactions
      .filter((txn) => txn.outstandingAmount !== undefined && txn.outstandingAmount > 0)
      .reduce((sum, txn) => sum + (txn.outstandingAmount || 0), 0);
    const outstandingBalance = baseOutstandingBalance * consolidatedMultiplier;
    const outstandingCount = (txSummary?.totalTransactions ?? customerTransactions.filter(
      (txn) => txn.outstandingAmount !== undefined && txn.outstandingAmount > 0
    ).length) * consolidatedMultiplier;

    // Overdue Balance Calculation - Use server-side summary when available
    const baseOverdueBalance = txSummary?.totalOverdue ?? customerTransactions
      .filter((txn) => txn.status === "overdue" && txn.outstandingAmount !== undefined && txn.outstandingAmount > 0)
      .reduce((sum, txn) => sum + (txn.outstandingAmount || 0), 0);
    const overdueBalance = baseOverdueBalance * consolidatedMultiplier;
    // For overdue count, fall back to client-side count when summary doesn't provide it
    const overdueCount = customerTransactions.filter((txn) =>
      txn.status === "overdue" && txn.outstandingAmount !== undefined && txn.outstandingAmount > 0
    ).length * consolidatedMultiplier;

    // Credit Available Calculation - Credit Limit minus Outstanding Balance
    // Note: Credit limit stays the same (per entity), but utilization is calculated against consolidated balance
    const availableCredit = customer.creditLimit - outstandingBalance;
    const creditUsedPercentage =
      customer.creditLimit > 0
        ? (outstandingBalance / customer.creditLimit) * 100
        : 0;

    // Payment Performance Calculation
    const paidInvoicesWithPayments = payments
      .filter((payment) => {
        if (!payment.invoiceDate) return false;
        const paymentDate = new Date(payment.date);
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
        return paymentDate >= twelveMonthsAgo;
      })
      .map((payment) => {
        const invoiceDate = new Date(payment.invoiceDate!);
        const paymentDate = new Date(payment.date);
        const daysDiff = Math.floor(
          (paymentDate.getTime() - invoiceDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        return daysDiff;
      });

    const averagePaymentDays =
      paidInvoicesWithPayments.length > 0
        ? Math.round(
            paidInvoicesWithPayments.reduce((sum, days) => sum + days, 0) /
              paidInvoicesWithPayments.length
          )
        : customer.dso;

    // Last payment date
    const sortedPayments = [...payments].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    const lastPaymentDate =
      sortedPayments.length > 0 ? sortedPayments[0].date : customer.lastPayment;

    return {
      outstandingBalance,
      outstandingCount,
      overdueBalance,
      overdueCount,
      availableCredit,
      creditUsedPercentage,
      averagePaymentDays,
      lastPaymentDate,
    };
  }, [customer, customerTransactions, txSummary, payments, consolidatedMultiplier]);

  // Get progress bar accent based on credit usage
  const getCreditProgressAccent = (
    percentage: number
  ): "success" | "warning" | "destructive" => {
    if (percentage >= 80) return "destructive";
    if (percentage >= 50) return "warning";
    return "success";
  };

  // Extract payment terms number
  const paymentTermsNumber = customer.paymentTerms.replace("Net ", "");

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Outstanding Balance Card */}
      {(() => {
        // Both values come directly from the Finance system — no FX calculation.
        // detailCurrencyMode switches between customer trading currency and base currency.
        const isBase = detailCurrencyMode === "base";
        const displayBalance = metrics.outstandingBalance; // Finance system supplies both; mock carries one value
        const displayCurrency = isBase ? "GBP" : customer.currency;
        return (
          <Card
            className="cursor-pointer transition-all hover:shadow-md border-l-4 border-l-orange-500"
            onClick={() => onCardClick("outstanding")}
            role="button"
            tabIndex={0}
            aria-label={`Outstanding Balance: ${formatCurrencyDetailed(displayBalance, displayCurrency)}. Click to view details`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onCardClick("outstanding");
              }
            }}
          >
            <CardContent className="py-4 px-5">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="p-1.5 rounded-full bg-orange-100 shrink-0">
                  <PoundSterling className="h-4 w-4 text-orange-600" />
                </div>
                <p className="text-sm font-medium text-muted-foreground leading-none">
                  Outstanding Balance
                </p>
              </div>
              <p
                className="text-2xl font-bold mb-1.5 transition-opacity duration-150"
                style={{ opacity: isVisible ? 1 : 0 }}
              >
                {formatCurrencyDetailed(displayBalance, displayCurrency)}
              </p>
              <p className="text-xs text-muted-foreground">
                {metrics.outstandingCount}{" "}
                {metrics.outstandingCount === 1
                  ? "outstanding invoice"
                  : "outstanding invoices"}
              </p>
            </CardContent>
          </Card>
        );
      })()}

      {/* Overdue Balance Card */}
      {(() => {
        // Both values come directly from the Finance system — no FX calculation.
        // Switches in sync with the Outstanding Balance card via the same detailCurrencyMode.
        const isBase = detailCurrencyMode === "base";
        const displayBalance = metrics.overdueBalance; // Finance system supplies both; mock carries one value
        const displayCurrency = isBase ? "GBP" : customer.currency;
        return (
          <Card
            className="cursor-pointer transition-all hover:shadow-md border-l-4 border-l-red-500"
            onClick={() => onCardClick("overdue")}
            role="button"
            tabIndex={0}
            aria-label={`Overdue Balance: ${formatCurrencyDetailed(displayBalance, displayCurrency)}. Click to view details`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onCardClick("overdue");
              }
            }}
          >
            <CardContent className="py-4 px-5">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="p-1.5 rounded-full bg-red-100 shrink-0">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                </div>
                <p className="text-sm font-medium text-muted-foreground leading-none">
                  Overdue Balance
                </p>
              </div>
              <p
                className="text-2xl font-bold text-red-600 mb-1.5 transition-opacity duration-150"
                style={{ opacity: isVisible ? 1 : 0 }}
              >
                {formatCurrencyDetailed(displayBalance, displayCurrency)}
              </p>
              <p className="text-xs text-muted-foreground">
                {metrics.overdueCount === 0
                  ? "No overdue invoices"
                  : `${metrics.overdueCount} ${
                      metrics.overdueCount === 1 ? "overdue invoice" : "overdue invoices"
                    }`}
              </p>
            </CardContent>
          </Card>
        );
      })()}

      {/* Credit Available Card */}
      {(() => {
        // Both values come directly from the Finance system — no FX calculation.
        // Switches in sync with Outstanding/Overdue cards via the same detailCurrencyMode.
        // The utilisation percentage is currency-agnostic and remains unchanged.
        const isBase = detailCurrencyMode === "base";
        const displayCurrency = isBase ? "GBP" : customer.currency;
        const displayAvailable = metrics.availableCredit;  // Finance system supplies both; mock carries one value
        const displayLimit = customer.creditLimit;          // Finance system supplies both; mock carries one value
        const hasNoCreditLimit = customer.creditLimit === 0;
        return (
          <Card
            className="cursor-pointer transition-all hover:shadow-md border-l-4 border-l-green-500"
            onClick={() => onCardClick("credit")}
            role="button"
            tabIndex={0}
            aria-label={hasNoCreditLimit ? "Credit Available: No Credit Limit Set. Click to view details" : `Credit Available: ${formatCreditLimit(displayAvailable, displayCurrency)}. Click to view details`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onCardClick("credit");
              }
            }}
          >
            <CardContent className="py-4 px-5">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="p-1.5 rounded-full bg-green-100 shrink-0">
                  <CreditCard className="h-4 w-4 text-green-600" />
                </div>
                <p className="text-sm font-medium text-muted-foreground leading-none">
                  Credit Available
                </p>
              </div>
              {hasNoCreditLimit ? (
                <p className="text-lg font-medium text-muted-foreground mb-1.5">No Credit Limit Set</p>
              ) : (
                <>
                  <p
                    className="text-2xl font-bold text-green-600 mb-1.5 transition-opacity duration-150"
                    style={{ opacity: isVisible ? 1 : 0 }}
                  >
                    {formatCreditLimit(displayAvailable, displayCurrency)}
                  </p>
                  <p
                    className="text-xs text-muted-foreground mb-2 transition-opacity duration-150"
                    style={{ opacity: isVisible ? 1 : 0 }}
                  >
                    Limit: {formatCreditLimit(displayLimit, displayCurrency)} (
                    {metrics.creditUsedPercentage.toFixed(1)}% used)
                  </p>
                  <Progress
                    value={metrics.creditUsedPercentage}
                    className="h-1.5"
                    accent={getCreditProgressAccent(metrics.creditUsedPercentage)}
                  />
                </>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Payment Performance Card */}
      <Card
        className="cursor-pointer transition-all hover:shadow-md border-l-4 border-l-blue-500"
        onClick={() => onCardClick("performance")}
        role="button"
        tabIndex={0}
        aria-label={`Payment Performance: ${metrics.averagePaymentDays} days average. Click to view details`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onCardClick("performance");
          }
        }}
      >
        <CardContent className="py-4 px-5">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="p-1.5 rounded-full bg-blue-100 shrink-0">
              <TrendingUp className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-sm font-medium text-muted-foreground leading-none">
              Payment Performance
            </p>
          </div>
          <p className="text-2xl font-bold mb-1.5">{metrics.averagePaymentDays} days</p>
          <p className="text-xs text-muted-foreground mb-0.5">
            Average payment time • Terms: Net {paymentTermsNumber}
          </p>
          <p className="text-xs text-muted-foreground">
            Last payment: {formatDateShort(metrics.lastPaymentDate)}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
